import React from 'react';
import classNames from 'classnames';
import styled from 'styled-components';
import Icon from '../icon';
import Button, { SquareButton, ButtonProps } from '../button';

interface VideoPlayerProps extends Omit<React.VideoHTMLAttributes<any>, 'onEnded'> {
  src?: string;
  type?: 'video/mp4' | 'video/avi' | 'video/mov' | 'video/quicktime';
  className?: string;
  block?: boolean;
  pauseButton?: React.JSX.Element;
  playButton?: React.JSX.Element;
  stopPropagation?: boolean;
  preventDefault?: boolean;
  onEnded?: (e: Event) => void;
  disabled?: boolean;
}

const VideoPlayerComponent = ({
  src,
  type,
  className,
  block,
  onPlay,
  onEnded,
  autoPlay,
  muted,
  onWaiting,
  controls = true,
  loop,
  children,
  pauseButton,
  playButton,
  stopPropagation,
  preventDefault,
  disabled,
}: VideoPlayerProps) => {
  const [isPlaying, setIsPlaying] = React.useState(false);
  const playerRef = React.useRef<HTMLVideoElement>(null);

  const onPlayHandler = (e: React.MouseEvent<HTMLVideoElement>) => {
    if (stopPropagation) {
      e.stopPropagation();
    }

    if (preventDefault) {
      e.preventDefault();
    }

    setIsPlaying(true);

    playerRef?.current?.play();
  };

  const onPauseHandler = (e: React.MouseEvent<HTMLVideoElement>) => {
    if (stopPropagation) {
      e.stopPropagation();
    }

    if (preventDefault) {
      e.preventDefault();
    }

    setIsPlaying(false);

    playerRef.current?.pause();
  };

  const onEndedHandler = (e: Event) => {
    setIsPlaying(false);
    onEnded?.(e);
  };

  React.useEffect(() => {
    if (playerRef.current) {
      playerRef.current.addEventListener('ended', onEndedHandler);
    }

    return function cleanup() {
      if (playerRef.current) {
        playerRef.current.removeEventListener('ended', onEndedHandler);
      }
    };
  }, []);

  return (
    <div data-testid={'video-player'} className={classNames({ block }, className)}>
      <video
        className={classNames('video-player', { block, disabled })}
        ref={playerRef}
        onPlay={onPlay}
        onWaiting={onWaiting}
        autoPlay={autoPlay}
        controls={disabled ? false : controls}
        muted={muted}
        loop={loop}
      >
        <track kind={'captions'} srcLang={'en'} label={'Captions'} />
        {src && <source src={src} type={type} />}
      </video>
      {pauseButton || playButton
        ? isPlaying
          ? pauseButton && React.cloneElement(pauseButton, { onClick: onPauseHandler })
          : playButton && React.cloneElement(playButton, { onClick: onPlayHandler })
        : null}
      {children}
    </div>
  );
};

const StyledControlButton = styled(props => <Button variant={'secondary-clean'} {...props} />)`
  height: 48px;
  width: 48px;
  padding: 0;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: calc(50% - 24px);
  right: calc(50% - 24px);
  cursor: pointer;
  transition: 0.2s;
  background: #ffffff;
  opacity: 0.8;
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.200691);

  &:hover {
    background: rgba(0, 0, 0, 0.7);
  }
`;

const PlayButton = (props: ButtonProps) => (
  <StyledControlButton {...props}>
    <Icon variant={'gray800'} name={'PLAY'} size={'xl'} aria-label={'play video'} />
  </StyledControlButton>
);

const PauseButton = (props: ButtonProps) => (
  <StyledControlButton variant={'clean'} {...props}>
    <Icon variant={'gray800'} name={'PAUSE'} size={'xl'} aria-label={'pause video'} />
  </StyledControlButton>
);

const RemoveButton = ({ ...rest }: Omit<ButtonProps, 'variant' | 'size'>) => (
  <SquareButton
    variant={'secondary'}
    size={'sm'}
    data-testid={'remove-button'}
    className={'remove-button'}
    aria-label={'remove video'}
    {...rest}
  >
    <Icon size="lg" variant="inherit" clickable name="TRASH" />
  </SquareButton>
);
const StyledVideoPlayer = styled(VideoPlayerComponent)`
  outline: none;
  position: relative;

  width: ${props => (typeof props.width === 'number' ? `${props.width}px` : props.width)};

  &.block {
    width: 100%;
  }

  > video {
    width: ${props => (typeof props.width === 'number' ? `${props.width}px` : props.width)};

    height: ${props => (props.height ? `${props.height}px` : 'auto')};
    max-width: 100%;

    &.block {
      width: 100%;
    }
  }

  .video-player {
    &.block {
      width: 100%;
    }

    &.hidden {
      visibility: hidden;
      height: 1px;
    }

    &.disabled {
      pointer-events: none;
    }
  }

  .remove-button {
    position: absolute;
    top: 8px;
    right: 8px;
  }
`;

const VideoPlayer: React.FunctionComponent<VideoPlayerProps> & {
  PlayButton: React.FunctionComponent<ButtonProps>;
  PauseButton: React.FunctionComponent<ButtonProps>;
  RemoveButton: React.FunctionComponent<ButtonProps>;
} = props => <StyledVideoPlayer {...props} />;

VideoPlayer.PlayButton = PlayButton;
VideoPlayer.PauseButton = PauseButton;
VideoPlayer.RemoveButton = RemoveButton;

export default VideoPlayer;
